import { FC } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import moment from 'moment';
import HelpTileContainer from 'features/dashboard/helpTilesContainer';
import { RootState } from 'app/store';
import { getDateRangeAsObjects } from 'features/date-range-selection/dateRangeSlice';
import { CountResponse, useGetVisitorCountQuery } from 'app/apis/queryApi';
import { NavLink } from 'react-router-dom';
import { closeHelpTile } from 'features/dashboard/helpTileSlice';
import TileGridContainer from 'features/tiles/tileGridContainer';
import ROIMetricTile from 'features/dashboard/roiMetricTile';
import RevenueMetricTile from 'features/dashboard/revenueMetricTile';
import CostMetricTile from 'features/dashboard/costMetricTile';
import SalesMetricTile from 'features/dashboard/salesMetricTile';
import FormAndPhoneMetricTile from 'features/dashboard/formAndPhoneMetricTile';
import PhoneCallMetricTile from 'features/dashboard/phoneCallMetricTile';
import FormMetricTile from 'features/dashboard/formMetricTile';
import VisitorMetricTile from 'features/dashboard/visitorMetricTile';
import DashboardChart from 'features/dashboard/dashboardChart';
import AverageVisitorLifetimeValueTile from 'features/dashboard/averageVisitorLifetimeValueTile';
import { ToggleType } from 'features/internal/internal.models';
import { debugToggleByType } from 'features/internal/internal-settings.slice';
import FakeTile from 'features/dashboard/fakeTile';

const Dashboard: FC<Record<string, unknown>> = () => {
  const dateRange = useSelector(getDateRangeAsObjects);
  const { siteId } = useSelector((state: RootState) => state.selectedSite);

  const dashboardPOCToggles = useSelector((state: RootState) =>
    debugToggleByType(state, ToggleType.DisplayPOCDashboardTiles)
  );

  const showDashboardPOCToggles = dashboardPOCToggles?.enabled ?? false;

  const { helpTileHasClosed } = useSelector(
    (state: RootState) => state.helpTile
  );

  const dispatch = useDispatch();

  const {
    data: visitorData,
    isLoading: visitorIsLoading,
    isSuccess: visitorQueryIsFinished
  } = useGetVisitorCountQuery({
    siteId,
    startDate: moment(dateRange.startDate).format('DD/MM/YYYY'),
    endDate: moment(dateRange.endDate).format('DD/MM/YYYY')
  });

  const shouldDisplayHelp = (): boolean => {
    function doesDataHaveAnyValueGreaterThanZero(
      isDataLoading: boolean,
      data: CountResponse | undefined
    ): boolean {
      return (
        data !== undefined &&
        !isDataLoading &&
        Object.values(data) !== undefined &&
        Object.values(data).some((value) => value > 0)
      );
    }

    const isHelpNeeded =
      visitorQueryIsFinished &&
      !doesDataHaveAnyValueGreaterThanZero(
        visitorIsLoading ?? true,
        visitorData
      );
    return isHelpNeeded;
  };

  return (
    <>
      {!helpTileHasClosed && shouldDisplayHelp() ? (
        <HelpTileContainer closeHelp={() => dispatch(closeHelpTile())} />
      ) : null}
      <>
        <TileGridContainer className="grid grid-cols-12 gap-3">
          <DashboardChart />

          <div className="col-span-12 md:col-span-6 lg:col-span-4 flex items-stretch">
            <VisitorMetricTile />
          </div>

          <NavLink
            to={'/conversions/form'}
            className="col-span-12 md:col-span-6 lg:col-span-4 flex items-stretch"
          >
            <FormMetricTile />
          </NavLink>

          <div className="col-span-12 md:col-span-6 lg:col-span-4 flex items-stretch">
            <NavLink
              to={'/conversions/phone-call'}
              className="col-span-12 md:col-span-6 lg:col-span-4 flex items-stretch grow"
            >
              <PhoneCallMetricTile />
            </NavLink>
          </div>
          <div className="col-span-12 md:col-span-6 lg:col-span-4 flex items-stretch">
            <FormAndPhoneMetricTile />
          </div>
          <NavLink
            to={'/revenue'}
            className="col-span-12 md:col-span-6 lg:col-span-4 flex items-stretch"
          >
            <SalesMetricTile />
          </NavLink>
          <div className="col-span-12 md:col-span-6 lg:col-span-4 flex items-stretch">
            <CostMetricTile />
          </div>

          <NavLink
            to={'/revenue'}
            className="col-span-12 md:col-span-6 lg:col-span-4 flex items-stretch"
          >
            <RevenueMetricTile />
          </NavLink>
          <NavLink
            to={'/revenue'}
            className="col-span-12 md:col-span-6 lg:col-span-4 flex items-stretch"
          >
            <ROIMetricTile />
          </NavLink>
          <div className="col-span-12 md:col-span-6 lg:col-span-4 flex items-stretch">
            <AverageVisitorLifetimeValueTile />
          </div>
          {showDashboardPOCToggles ? (
            <>
              <div className="col-span-12 md:col-span-6 lg:col-span-4 flex items-stretch">
                <FakeTile
                  tileData={{
                    label: 'Customer Acquisition Cost',
                    count: 1273.38,
                    metric: '£58.49 above annual average (£1,214.89)',
                    format: new Intl.NumberFormat('en-GB', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2
                    }),
                    currencyCode: '£',
                    percentageDiff: 4.81
                  }}
                />
              </div>
              <div className="col-span-12 md:col-span-6 lg:col-span-4 flex items-stretch">
                <FakeTile
                  tileData={{
                    label: 'Average Days To Conversion',
                    count: 3.48,
                    metric: '3.74 annual average',
                    format: new Intl.NumberFormat('en-GB', {})
                  }}
                />
              </div>
              <div className="col-span-12 md:col-span-6 lg:col-span-4 flex items-stretch">
                <FakeTile
                  tileData={{
                    label: 'Average Days To Revenue',
                    count: 42.48,
                    metric: '38.93 annual average',
                    format: new Intl.NumberFormat('en-GB', {})
                  }}
                />
              </div>
            </>
          ) : (
            <></>
          )}
        </TileGridContainer>
      </>
    </>
  );
};

export default Dashboard;
